import { css } from "styled-components";
import { pxToRem } from "@input-output-hk/px-to-rem";
import colors from "../tokens/colors";

export const osanoVariables = css`
  --osano-dialog-max-width: ${pxToRem(960)};
  --osano-dialog-elastic-padding: calc(
    (100% - min(100vw, var(--osano-dialog-max-width))) / 2
  );
  --osano-dialog-magic-padding: max(
    var(--osano-dialog-elastic-padding),
    ${pxToRem(20)}
  );
  --osano-cookie-widget-color: linear-gradient(
    94.22deg,
    ${colors.primary.electricBlue} -18.3%,
    ${colors.others.skyBlue} 118.89%
  );
  --osano-close-color: ${colors.primary.slateGrey};
  --osano-link-color: ${colors.primary.electricBlue};
  --osano-font-family: var(--default-text-font-family), sans-serif;
  --osano-font-color: ${colors.primary.slateGrey};
  --osano-background-color: ${colors.secondary.mondayMist};
  --osano-btn-color: ${colors.primary.electricBlue};
  --osano-btn-bg-color: #fff;
  --osano-btn-hover-color: #fff;
  --osano-btn-bg-hover-color: ${colors.primary.electricBlue};
  --osano-switch-color: #e0e0e0;
  --osano-switch-active-color: #000000;
  --osano-border-gradient: linear-gradient(
    94.22deg,
    ${colors.primary.cardanoBlue} -18.3%,
    ${colors.primary.cardanoBlue} 118.89%
  );
  --osano-btn-border-gradient: linear-gradient(
    94.22deg,
    ${colors.primary.electricBlue} -18.3%,
    ${colors.primary.electricBlue} 118.89%
  );
  --osano-background-gradient: linear-gradient(
        var(--osano-background-color),
        var(--osano-background-color)
      )
      padding-box,
    var(--osano-border-gradient) border-box;

  --osano-btn-bg-gradient: linear-gradient(
        var(--osano-btn-bg-color),
        var(--osano-btn-bg-color)
      )
      padding-box,
    var(--osano-btn-border-gradient) border-box;
`;

export const osanoStyling = css`
  /* Parent element */
  .osano-cm-window {
    font-family: var(--osano-font-family);
    /**
     * Initial Dialog
     * --------------
     *
     * 1. Make font color consistent
     * 2. Override background for gradient border effect
     *     - We can't use 'max-width: xxx; margin: auto' to center
     * 3. Align to center
     * 4. Make mobile whitespace consistent
     * 5. Make desktop whitespace consistent
     */
    .osano-cm-dialog {
      color: var(--osano-font-color); /* 1 */
      border-radius: ${pxToRem(16)}; /* 2 */
      border: ${pxToRem(2)} solid transparent; /* 2 */
      background: var(--osano-background-gradient); /* 2 */
      margin-left: var(--osano-dialog-magic-padding); /* 3 */
      margin-right: var(--osano-dialog-magic-padding); /* 3 */
      padding: ${pxToRem(30)}; /* 4 */
      margin-bottom: ${pxToRem(20)}; /* 4 */
      @media screen and (min-width: 768px) {
        padding: ${pxToRem(60)}; /* 5 */
        margin-bottom: ${pxToRem(40)}; /* 5 */
      }
    }
    /**
     * Button
     * ------
     *
     * 1. Make text consistent
     * 2. Gradient border effect
     * 3. Align size with site design
     * 4. Align hover effect with site design
     */
    .osano-cm-button {
      font-family: var(--osano-font-family); /* 1 */
      color: var(--osano-btn-color); /* 1 */
      background-color: var(--osano-btn-bg-color); /* 2 */
      border: ${pxToRem(2)} solid transparent; /* 2 */
      border-radius: ${pxToRem(14)}; /* 2 */
      background: var(--osano-btn-bg-gradient); /* 2 */
      height: ${pxToRem(44)}; /* 3 */
      transition: transform 0.1s ease-out, box-shadow 0.1s ease-out; /* 4 */
      &:hover:not(:disabled):not(:active):not([aria-selected="true"]) {
        transform: scale(1.035); /* 4 */
        box-shadow: ${pxToRem(0, 4, 10)} rgba(167, 143, 160, 0.2); /* 4 */
      }
    }
    /* Button: focus/hover */
    .osano-cm-button:focus,
    .osano-cm-button:hover {
      color: var(--osano-btn-hover-color);
      background: var(--osano-btn-bg-hover-color);
    }
    /* Buttons in group */
    .osano-cm-dialog--type_bar .osano-cm-button {
      width: 100%;
    }
    /**
     * When buttons are in a stack...
     * 1. Make mobile whitespace consistent
     * 2. Make desktop whitespace consistent
     */
    .osano-cm-dialog--type_bar .osano-cm-button + .osano-cm-button {
      margin-top: calc(${pxToRem(10)} - 0.25em); /* 1 */
      @media screen and (min-width: 768px) {
        margin-top: calc(${pxToRem(20)} - 0.25em); /* 2 */
      }
    }
    /**
     * 1. Remove user-agent defaults for link color
     */
    .osano-cm-link,
    .osano-cm-link:hover {
      color: var(--osano-link-color); /* 1 */
      font-weight: bold;
    }
    /**
     * Toggle Switch
     * -------------
     * 1. Align colors
     */
    .osano-cm-toggle__switch {
      background-color: var(--osano-switch-color); /* 1 */
    }
    /* Toggle switch: focus/hover */
    .osano-cm-toggle__input:focus + .osano-cm-toggle__switch,
    .osano-cm-toggle__input:hover + .osano-cm-toggle__switch {
      background-color: var(--osano-switch-color); /* 1 */
      border-color: var(--osano-switch-color); /* 1 */
    }
    /* Toggle switch: checked: focus/hover */
    .osano-cm-toggle__input:checked:focus + .osano-cm-toggle__switch,
    .osano-cm-toggle__input:checked:hover + .osano-cm-toggle__switch,
    .osano-cm-toggle__input:checked + .osano-cm-toggle__switch {
      background-color: var(--osano-switch-active-color); /* 1 */
      border-color: var(--osano-switch-active-color); /* 1 */
      &:after {
        background-color: var(--osano-btn-bg-color); /* 1 */
        border-color: var(--osano-btn-bg-color); /* 1 */
      }
    }
    /**
     * View cookies dropdown
     * ---------------------
     */
    .osano-cm-disclosure__toggle,
    .osano-cm-disclosure__toggle:hover {
      color: inherit; /* 1 */
    }
    /* 1. Fix horrible info-dialog shadow */
    .osano-cm-info {
      box-shadow: 0 0 12px 6px rgba(0, 0, 0, 0.15); /* 1. */
    }
    /**
     * 1. Hide Osano logo
     */
    .osano-cm-view__powered-by {
      display: none; /* 1 */
    }
    /**
     * Sidebar styling:
     * ----------------
     */
    .osano-cm-info-dialog__info {
      /**
       * 1. Make wider on desktop
       */
      @media screen and (min-width: 768px) {
        max-width: ${pxToRem(465)}; /* 1 */
      }
      /**
       * 1. Remove sticky behaviour
       * 2. Adjust desktop close button position based on whitespace change
       */
      .osano-cm-info-dialog-header {
        position: relative; /* 1 */
        @media screen and (min-width: 768px) {
          .osano-cm-close {
            margin-top: ${pxToRem(-30)}; /* 2 */
          }
        }
      }
      /**
       * 1. Make sidebar title bold
       * 2. Make desktop padding consistent with content
       */
      .osano-cm-info-dialog-header__header {
        font-weight: bold; /* 1 */
        @media screen and (min-width: 768px) {
          padding-top: ${pxToRem(60)}; /* 2 */
          padding-left: ${pxToRem(60)}; /* 2 */
        }
      }
      /**
       * 1. Make text more readable
       */
      .osano-cm-description {
        font-size: ${pxToRem(14)}; /* 1 */
        line-height: 1.64; /* 1 */
      }
      /**
       * 1. Add whitespace to bottom of dialog
       */
      .osano-cm-save.osano-cm-view__button {
        margin-bottom: ${pxToRem(60)}; /* 1 */
      }
    }
    /**
     * 1. Make mobile whitespace consistent
     * 2. Make desktop whitespace consistent
     */
    .osano-cm-view--type_consent {
      padding: 0 ${pxToRem(20)}; /* 1 */
      @media screen and (min-width: 768px) {
        padding: 0 ${pxToRem(60)}; /* 2 */
      }
    }
    /**
     * 1. Make section headings bold
     */
    .osano-cm-toggle__label[role="heading"] {
      font-weight: bold; /* 1 */
    }
    /**
     * 1. Make whitespace consistent with parent
     */
    .osano-cm-disclosure {
      padding-left: 0; /* 1 */
      padding-right: 0; /* 1 */
      margin-left: 0; /* 1 */
      margin-right: 0; /* 1 */
      margin-bottom: ${pxToRem(30)}; /* 1 */
    }
    /**
     * 1. Make font-size consistent
     * 2. Add preceeding dropdown arrow
     */
    .osano-cm-disclosure__toggle {
      font-size: ${pxToRem(14)}; /* 1 */
      position: relative; /* 2 */
      &:before {
        display: inline-block; /* 2 */
        content: ""; /* 2 */
        position: relative; /* 2 */
        width: ${pxToRem(12.5)}; /* 2 */
        height: ${pxToRem(8.5)}; /* 2 */
        margin-right: ${pxToRem(8)}; /* 2 */
        background: url("data:image/svg+xml;base64, PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNCIgaGVpZ2h0PSI5IiBmaWxsPSJub25lIj48cGF0aCBzdHJva2U9IiMwMDAiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIgc3Ryb2tlLXdpZHRoPSIyLjUiIGQ9Im0yIDEuNzUgNSA1LjUgNS01LjUiLz48L3N2Zz4=")
          center center no-repeat; /* 2 */
      }
    }
    /**
     * 1. Override cookie widget icon with custom SVG
     */
    .osano-cm-window__widget {
      background: url("data:image/svg+xml;base64,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")
        center center no-repeat; /* 1 */
      background-size: ${pxToRem(40)}; /* 1 */
      > svg {
        display: none; /* 1 */
      }
    }
    /**
     * 1. Remove cookie widget icon focus outline
     */
    .osano-cm-widget:focus {
      outline: none; /* 1 */
    }
    /**
     * 1. Override default close button colors
     */
    .osano-cm-dialog__close {
      color: var(--osano-close-color); /* 1 */
      stroke: var(--osano-close-color); /* 1 */
    }
  }

  .osano-cm-disclosure--collapse {
    border-bottom: 0;
    padding-bottom: 0;
  }

  .osano-cm-disclosure {
    margin: 0;
    padding: 0;
  }

  .osano-cm-toggle {
    padding-top: 1.5em;
  }

  label[for="osano-cm-drawer-toggle--category_MARKETING"] {
    display: none;
  }

  p[id="osano-cm-drawer-toggle--category_MARKETING--description"] {
    display: none;
  }

  div[aria-controls="osano-cm-MARKETING_disclosures"] {
    display: none;
  }

  ul[id="osano-cm-MARKETING_disclosures"] {
    display: none;
  }

  label[for="osano-cm-dialog-toggle--category_MARKETING"] {
    display: none;
  }

  label[for="osano-cm-drawer-toggle--category_PERSONALIZATION"] {
    display: none;
  }

  p[id="osano-cm-drawer-toggle--category_PERSONALIZATION--description"] {
    display: none;
  }

  div[aria-controls="osano-cm-PERSONALIZATION_disclosures"] {
    display: none;
  }

  ul[id="osano-cm-PERSONALIZATION_disclosures"] {
    display: none;
  }

  label[for="osano-cm-dialog-toggle--category_PERSONALIZATION"] {
    display: none;
  }
`;
